// Du besoin à l'application (étape 2) : lecture d'un fichier « ekera-application-cible », libellés et lignes d'affichage.
// Aucun calcul ici : tout chiffre (portes d'entrée, arrêt maximal, pertes potentielles estimées, indicateur NIS2) vient
// de la réponse de l'API, qui vient du moteur. Ce module ne fait que lire, retrouver des noms et mettre en forme.
import { ErreurApi, expliquerErreur } from '../api/client';
import type { ApplicationCible, Carte, Citation, EffetApplication, Lien } from '../api/types';
import { eurosCourt, LIBELLE_LIEN, LIBELLE_TYPE } from './format';

export const MAX_VARIANTES = 3;
/** L'API refuse un corps de plus de 64 Kio (413) : on le dit avant l'envoi. */
export const TAILLE_MAX_OCTETS = 64 * 1024;

export type Application = ApplicationCible;
export type ObjetApplicationCible = NonNullable<Application['objets']>[number];

export const MESSAGE_TROP_GROS = 'Le fichier dépasse 64 Kio : l’API refuserait la demande. Réduisez l’application (40 objets et 120 liens au plus).';

/** « 1 citation », « 21 citations ». */
export function pluriel(n: number, mot: string): string {
  return `${n} ${mot}${n > 1 ? 's' : ''}`;
}

export type LectureApplication = { application: Application } | { erreur: string };

/**
 * Lit le texte d'un fichier ou d'un collage. Seules la forme et la taille sont vérifiées ; les règles (citations
 * comprises) sont celles de l'API, qui refuse ce qui ne les respecte pas.
 */
export function lireApplication(texte: string): LectureApplication {
  if (new TextEncoder().encode(texte).length > TAILLE_MAX_OCTETS)
    return { erreur: MESSAGE_TROP_GROS };
  let brut: unknown;
  try {
    brut = JSON.parse(texte);
  } catch {
    return { erreur: 'Ce contenu n’est pas du JSON lisible.' };
  }
  if (typeof brut !== 'object' || brut === null || Array.isArray(brut)) return { erreur: 'Le contenu attendu est un objet JSON au format « ekera-application-cible ».' };
  const o = brut as Record<string, unknown>;
  if (o.format !== 'ekera-application-cible') return { erreur: 'Ce fichier n’est pas au format « ekera-application-cible ».' };
  if (typeof o.id !== 'string' || typeof o.nom !== 'string') return { erreur: 'L’application doit porter un identifiant (« id ») et un nom (« nom »).' };
  const besoin = o.besoin as Record<string, unknown> | undefined;
  if (!besoin || typeof besoin.id !== 'string' || typeof besoin.texte !== 'string') return { erreur: 'L’application doit porter son besoin (« besoin » avec « id » et « texte »).' };
  if (o.statut !== undefined && o.statut !== 'propose') return { erreur: 'Seule une application au statut « propose » peut être essayée : une application retenue ou abandonnée est refusée par l’API.' };
  return { application: o as unknown as Application };
}

/** Explication de chaque refus 422 de la route (codes de l'API), suivie du message de l'API qui ne recopie jamais la valeur. */
export const LIBELLE_REFUS_APPLICATION: Record<string, string> = {
  statut_non_propose: 'L’application et chacun de ses éléments doivent être au statut « propose ».',
  element_hors_forme: 'Un élément est hors forme (extension, retrait, version ou provenance par attribut non admis).',
  identifiant_existant: 'Un identifiant existe déjà sur la carte.',
  identifiant_en_double: 'Un identifiant est utilisé deux fois dans l’application.',
  identifiant_invalide: 'Un identifiant n’a pas la forme attendue.',
  type_non_permis: 'Un type d’objet ou de lien n’est pas permis pour une application cible.',
  objet_inconnu: 'Un lien désigne un objet qui n’existe ni sur la carte ni dans l’application.',
  lien_sur_la_carte_existante: 'Un lien relie deux objets déjà présents sur la carte.',
  lien_incoherent: 'Un lien n’est pas cohérent avec les types de ses deux bouts.',
  passage_existant: 'Un passage réseau existe déjà sur la carte.',
  zone_manquante: 'Un composant nouveau n’est dans aucune zone.',
  zone_multiple: 'Un composant nouveau est dans plusieurs zones.',
  fournisseur_multiple: 'Un composant nouveau a plusieurs fournisseurs.',
  citation_manquante: 'Un élément ou un lien ne cite pas sa source.',
  citations_trop_nombreuses: 'Un élément cite plus de cinq sources.',
  citation_non_verifiee: 'Une citation n’a pas été retrouvée mot pour mot dans sa source : rien n’est calculé tant qu’elle n’est pas corrigée.',
  ajout_refuse: 'Le moteur refuse l’ajout de l’application à la carte.',
  lien_source_existante: 'Un lien part d’un objet existant sans en créer de nouveau.',
  calcul_impossible: 'Le moteur ne peut pas calculer l’effet de cette application.',
};

/** Texte vu par la personne pour un refus de l'essai : le motif en français, puis le message de l'API. */
export function expliquerRefusApplication(e: unknown): string {
  if (e instanceof ErreurApi && e.statut === 422 && LIBELLE_REFUS_APPLICATION[e.code])
    return `${LIBELLE_REFUS_APPLICATION[e.code]} ${e.message}`.trim();
  return expliquerErreur(e);
}

/** Date du besoin, sans fuseau (jour seulement) : « 24 oct. 2026 ». */
export function jourBesoin(iso: string | null | undefined): string {
  if (!iso) return 'date non indiquée';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium', timeZone: 'UTC' }).format(d);
}

export type StatutElement = 'nouveau' | 'reutilise';
export const LIBELLE_STATUT_ELEMENT: Record<StatutElement, string> = { nouveau: 'nouveau', reutilise: 'réutilisé' };

export interface LigneArchitecture {
  id: string;
  nom: string;
  type: string;
  statut: StatutElement;
  citations: Citation[];
  /** Pour un objet réutilisé : les liens de l'application qui le citent (ses citations viennent d'eux). */
  viaLiens: string[];
}

export interface LigneFlux {
  id: string;
  de: string;
  vers: string;
  nature: string;
  detail: string | null;
  citations: Citation[];
}

function nomsDeLaCarte(carte: Carte | undefined): Map<string, { nom: string; type: string }> {
  return new Map((carte?.objets ?? []).map((o) => [o.id, { nom: o.nom, type: LIBELLE_TYPE[o.type] ?? o.type }]));
}

function nomsDeLApplication(app: Application): Map<string, { nom: string; type: string }> {
  return new Map((app.objets ?? []).map((o) => [o.id, { nom: o.nom, type: LIBELLE_TYPE[o.type] ?? o.type }]));
}

/** Nom lisible d'un objet de l'application ou de la carte ; à défaut son identifiant (jamais inventé). */
export function nomObjet(id: string, app: Application, carte: Carte | undefined): string {
  return nomsDeLApplication(app).get(id)?.nom ?? nomsDeLaCarte(carte).get(id)?.nom ?? id;
}

function motifDuLien(l: Lien | NonNullable<Application['liens']>[number]): string | null {
  const m = (l.attributs as Record<string, unknown> | undefined)?.motif;
  return typeof m === 'string' ? m : null;
}

/** Éléments de l'architecture : les objets de l'application (nouveaux), puis les objets de la carte qu'elle réutilise. */
export function lignesArchitecture(app: Application, effet: EffetApplication | undefined, carte: Carte | undefined): LigneArchitecture[] {
  const nouveaux: LigneArchitecture[] = (app.objets ?? []).map((o) => ({
    id: o.id,
    nom: o.nom,
    type: LIBELLE_TYPE[o.type] ?? o.type,
    statut: 'nouveau',
    citations: (o.provenance?.citations ?? []) as Citation[],
    viaLiens: [],
  }));
  const carteNoms = nomsDeLaCarte(carte);
  const reutilises: LigneArchitecture[] = (effet?.objets_reutilises ?? []).map((id) => {
    const liens = (app.liens ?? []).filter((l) => l.source === id || l.cible === id);
    return {
      id,
      nom: carteNoms.get(id)?.nom ?? id,
      type: carteNoms.get(id)?.type ?? '',
      statut: 'reutilise',
      citations: liens.flatMap((l) => (l.provenance?.citations ?? []) as Citation[]),
      viaLiens: liens.map((l) => l.id),
    };
  });
  return [...nouveaux, ...reutilises];
}

/** Liens de l'application (flux, dépendances, passages), avec les noms des deux bouts et leurs citations. */
export function lignesFlux(app: Application, carte: Carte | undefined): LigneFlux[] {
  return (app.liens ?? []).map((l) => ({
    id: l.id,
    de: nomObjet(l.source, app, carte),
    vers: nomObjet(l.cible, app, carte),
    nature: LIBELLE_LIEN[l.type] ?? l.type,
    detail: motifDuLien(l),
    citations: (l.provenance?.citations ?? []) as Citation[],
  }));
}

type Fourchette = { bas?: { valeur: number } | null; haut?: { valeur: number } | null } | null | undefined;

/** « 308 k€ à 2,2 M€ » : les deux bouts de la fourchette de l'API, jamais le haut seul ; « — » si l'API n'en rend pas. */
export function fourchetteVue(f: Fourchette, formater: (bas: number, haut: number) => string): string {
  return f?.bas && f?.haut ? formater(f.bas.valeur, f.haut.valeur) : '—';
}

/** Écart de la fourchette tel que l'API le donne (déjà calculé par le moteur) : seuls le signe et l'unité sont écrits. */
export function ecartFourchetteVue(f: { bas?: number | null; haut?: number | null } | undefined): string {
  if (!f || f.bas == null || f.haut == null) return '—';
  if (f.bas === 0 && f.haut === 0) return 'inchangé';
  const s = (x: number) => (x === 0 ? eurosCourt(0) : `${x > 0 ? '+' : '−'}${eurosCourt(Math.abs(x))}`);
  return `${s(f.bas)} à ${s(f.haut)}`;
}
